import { useEffect, useState } from "react";
import { api, INR, INR2 } from "@/lib/api";
import { FileText, ArrowDownLeft, ArrowUpRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_MAP = {
  BUY: { label: "Purchase", cls: "text-brand-secondary", Icon: ArrowDownLeft },
  KITTY: { label: "Kitty top-up", cls: "text-brand-secondary", Icon: ArrowDownLeft },
  SELL: { label: "Sold", cls: "text-brand-primary", Icon: ArrowUpRight },
  GIFT_OUT: { label: "Gifted", cls: "text-brand-primary", Icon: ArrowUpRight },
  REDEEM: { label: "Redeemed", cls: "text-brand-primary", Icon: ArrowUpRight },
};

export default function Vault() {
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      const [t, s] = await Promise.all([api.get("/wallet/transactions"), api.get("/wallet/summary")]);
      setTxns(t.data.transactions || []);
      setSummary(s.data);
    })();
  }, []);

  const downloadCert = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get("/vault/certificate", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers?.["content-disposition"] || "";
      const match = disp.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : "ShreeDhan-Certificate.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error("Could not download certificate");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pt-6">
      <div className="text-xs font-bold tracking-[0.22em] uppercase text-brand-primary">Vault</div>
      <h1 className="mt-1 font-display text-4xl font-semibold text-brand-cream">Your digital locker</h1>

      <div data-testid="vault-summary" className="mt-5 rounded-3xl border border-brand-line bg-brand-sand p-5 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-slate">Total holdings</div>
            <div className="mt-1 font-display text-3xl text-brand-cream tabular-nums">
              {summary ? Number(summary.grams).toFixed(4) : "—"}<span className="text-brand-accent"> g</span>
            </div>
            <div className="text-sm text-brand-slate tabular-nums">≈ {summary ? INR(summary.inr_value) : "—"}</div>
          </div>
          <button
            data-testid="vault-cert-download"
            onClick={downloadCert}
            disabled={downloading}
            className="h-11 rounded-full bg-gold-gradient text-brand-page px-4 flex items-center gap-2 text-sm font-semibold shadow-gold active:scale-95 disabled:opacity-70"
          >
            {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {downloading ? "Preparing…" : "Certificate"}
          </button>
        </div>
      </div>

      <h2 className="mt-8 font-display text-2xl text-brand-cream">Transactions</h2>
      <div data-testid="vault-txn-list" className="mt-3 divide-y divide-brand-line-faint rounded-2xl border border-brand-line-faint bg-brand-sand overflow-hidden">
        {txns.length === 0 && (
          <div className="p-6 text-brand-slate text-sm">No transactions yet.</div>
        )}
        {txns.map((t) => {
          const meta = TYPE_MAP[t.type] || { label: t.type, cls: "text-brand-slate", Icon: FileText };
          const Icon = meta.Icon;
          return (
            <div key={t.id} className="flex items-center gap-3 p-4">
              <div className="h-10 w-10 rounded-full bg-brand-sand2 flex items-center justify-center">
                <Icon size={16} className={meta.cls} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-cream text-sm">{meta.label}</div>
                <div className="text-xs text-brand-slate">{new Date(t.created_at).toLocaleString("en-IN")}</div>
              </div>
              <div className="text-right">
                <div className={`font-semibold tabular-nums text-sm ${meta.cls}`}>
                  {Number(t.grams || 0) >= 0 ? "+" : ""}{Number(t.grams || 0).toFixed(4)}g
                </div>
                <div className="text-xs text-brand-slate tabular-nums">{INR2(Math.abs(t.amount_inr || 0))}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
